import materialKeys from '@/api/queries/materials/keyFactory';
import {
  CATEGORIES,
  PreferredProductExtraColumn,
} from '@/ui/pages/addPreferredProductPage/AddPreferredProductPage';
import AddEditItemSidebar from '@/ui/pages/listedItemsPage/components/AddEditItemSidebar';
import ViewMaterialSidebar from '@/ui/pages/listedMaterialsPage/components/ViewMaterialSidebar';
import {
  Button,
  Card,
  ManageableItemsList,
  Modal,
  Tooltip,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import useRemoveAccountPreferredProduct from '../../../api/mutations/materials/useRemoveAccountPreferredProduct';
import useGetAccountPreferredProducts from '../../../api/queries/materials/useGetAccountPreferredProducts';
import { GetAccountPreferredProductsQuery } from '../../../gql/graphql';

type PreferredProductBase =
  GetAccountPreferredProductsQuery['accountPreferredProducts'][number];

type PreferredProduct = PreferredProductBase & { id: string };

const getPreferredOrCheapestSupplierMaterial = (
  item: PreferredProduct
): PreferredProduct['material']['suppliedBy'][number] | null => {
  const { suppliedBy, preferredSupplierId } = item.material;
  if (suppliedBy.length === 0) return null;

  if (preferredSupplierId) {
    const preferred = suppliedBy.find(
      (sm) => sm.supplier.id === preferredSupplierId
    );
    if (preferred) return preferred;
  }

  return suppliedBy.reduce((cheapest, sm) =>
    sm.price.unitPriceExcTax.value < cheapest.price.unitPriceExcTax.value
      ? sm
      : cheapest
  );
};

const ARCHIVED_PRODUCT_WARNING =
  'This product is no longer available and will not be recommended on new Estimates';

const PreferredProductNameCell: FC<{
  material: PreferredProduct['material'];
}> = (props) => {
  const { material } = props;
  const isArchived = material.productSpec?.archivedAt != null;

  return (
    <div className="flex items-center">
      <Tooltip
        tooltipContent={isArchived ? ARCHIVED_PRODUCT_WARNING : material.name}
        className={clstx(
          'max-w-[15vw] 2xl:max-w-[300px] overflow-hidden text-ellipsis',
          isArchived && 'text-gray-500'
        )}
      >
        {isArchived && (
          <span
            role="img"
            aria-label={ARCHIVED_PRODUCT_WARNING}
            className="text-yellow-500 mr-1.5"
          >
            <UntitledIcon
              name="alert-triangle.3"
              className="w-5 h-5 align-middle"
            />
          </span>
        )}
        {material.name}
      </Tooltip>
    </div>
  );
};

const CategoryTable: FC<{
  items: PreferredProduct[];
  isLoading: boolean;
  extraColumns?: readonly PreferredProductExtraColumn[];
  onDelete: (item: PreferredProduct) => void;
  onViewMaterial: (materialId: string) => void;
  onEditLineItem: (lineItemId: string) => void;
}> = (props) => {
  const {
    items,
    isLoading,
    extraColumns,
    onDelete,
    onViewMaterial,
    onEditLineItem,
  } = props;

  return (
    <ManageableItemsList.Table
      items={items}
      uniqueKey="id"
      isLoading={isLoading}
      emptyText="No devices added yet."
    >
      <ManageableItemsList.Table.Column
        field="material"
        header="Name"
        className="pr-0"
        render={(material: PreferredProduct['material']) => (
          <PreferredProductNameCell material={material} />
        )}
      />
      <ManageableItemsList.Table.Column
        field="material"
        header="Model"
        render={(material: PreferredProduct['material']) =>
          material.productSpec?.modelNumber ?? '-'
        }
      />
      {extraColumns?.map((column) => (
        <ManageableItemsList.Table.Column
          key={column.header}
          field="material"
          header={column.header}
          render={(material: PreferredProduct['material']) =>
            column.render(material.productSpec)
          }
        />
      ))}
      <ManageableItemsList.Table.Column
        field="material"
        header="Supplier cost (ex tax)"
        render={(_material, item: PreferredProduct) => {
          const supplierMaterial = getPreferredOrCheapestSupplierMaterial(item);
          if (!supplierMaterial) return null;
          return (
            <Button
              variant="link-inline"
              onClick={() => onViewMaterial(item.material.id)}
            >
              <CurrencyValue {...supplierMaterial.price.unitPriceExcTax} /> (
              {supplierMaterial.supplier.name})
            </Button>
          );
        }}
      />
      <ManageableItemsList.Table.Column
        field="lineItem"
        header="Customer price (inc tax)"
        render={(lineItem: PreferredProduct['lineItem']) => {
          if (!lineItem?.price) return null;
          return (
            <Button
              variant="link-inline"
              onClick={() => onEditLineItem(lineItem.id)}
            >
              <CurrencyValue {...lineItem.price.unitPrice} />
            </Button>
          );
        }}
      />
      <ManageableItemsList.Table.Column
        field="id"
        header=""
        render={(_id, item: PreferredProduct) => (
          <Tooltip tooltipContent="Remove from preferred products">
            <Button
              variant="ghost"
              colour="red"
              size="xs"
              className="w-[28px] h-[28px]"
              onClick={() => onDelete(item)}
              aria-label="Remove from preferred products"
            >
              <UntitledIcon name="trash-01.3" />
            </Button>
          </Tooltip>
        )}
      />
    </ManageableItemsList.Table>
  );
};

const DeletePreferredProductModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  material: PreferredProduct['material'] | null;
}> = (props) => {
  const { isOpen, onClose, material } = props;
  const { mutateAsync, isPending } = useRemoveAccountPreferredProduct();

  const onConfirm = useCallback(() => {
    if (!material) return;
    void mutateAsync(material.id).then(() => onClose());
  }, [material, mutateAsync, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Remove preferred product">
      <Modal.Body>
        Are you sure you want to remove <strong>{material?.name}</strong> from
        your preferred products? It will no longer be automatically added to new
        estimates.
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button variant="outline" onClick={() => onClose?.()}>
            Cancel
          </Button>
          <Button
            colour="red"
            isProcessing={isPending}
            onClick={() => !isPending && onConfirm()}
          >
            Yes, remove
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

const PreferredProductsSection: FC = () => {
  const { accountPreferredProducts, isLoading } =
    useGetAccountPreferredProducts();
  const [materialToDelete, setMaterialToDelete] = useState<
    PreferredProduct['material'] | null
  >(null);
  const [viewMaterialId, setViewMaterialId] = useState<string | null>(null);
  const [editLineItemId, setEditLineItemId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const history = useHistory();
  const { url } = useRouteMatch();

  // Material / supplier-material / line-item edits made inside either
  // sidebar can change any of the data we render here. Invalidate the
  // accountPreferredProducts cache (under `materialKeys.all`) so the list
  // refetches fresh on close.
  const refreshPreferredProducts = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: materialKeys.all });
  }, [queryClient]);

  const closeViewMaterialSidebar = useCallback(() => {
    setViewMaterialId(null);
    refreshPreferredProducts();
  }, [refreshPreferredProducts]);

  const closeEditLineItemSidebar = useCallback(() => {
    setEditLineItemId(null);
    refreshPreferredProducts();
  }, [refreshPreferredProducts]);

  const items: PreferredProduct[] = (accountPreferredProducts ?? []).map(
    (item) => ({ ...item, id: item.material.id })
  );

  const grouped = Map.groupBy(
    items,
    (item) => item.material.productSpec?.category ?? 'OTHER'
  );

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex flex-col gap-4">
        <div>
          <h2>Preferred Products</h2>
          <p>
            Add the heat pumps and water cylinders that you want to be used in
            heat pump Estimates. We will pick the items from this list which
            best match our calculation of the property's heat loss.
          </p>
        </div>
        {CATEGORIES.map(({ key, label, route, singular, extraColumns }) => (
          <ManageableItemsList key={key}>
            <ManageableItemsList.HeaderBar
              heading={label}
              buttons={
                <Button
                  size="sm"
                  onClick={() => history.push(`${url}/${route}`)}
                  variant="white"
                >
                  Add {singular}
                </Button>
              }
            />
            <CategoryTable
              items={grouped.get(key) ?? []}
              isLoading={isLoading}
              extraColumns={extraColumns}
              onDelete={(item) => setMaterialToDelete(item.material)}
              onViewMaterial={setViewMaterialId}
              onEditLineItem={setEditLineItemId}
            />
          </ManageableItemsList>
        ))}
        <DeletePreferredProductModal
          isOpen={materialToDelete !== null}
          onClose={() => setMaterialToDelete(null)}
          material={materialToDelete}
        />
        <ViewMaterialSidebar
          isOpen={viewMaterialId !== null}
          materialId={viewMaterialId ?? undefined}
          onClose={closeViewMaterialSidebar}
        />
        <AddEditItemSidebar
          isOpen={editLineItemId !== null}
          lineItemId={editLineItemId ?? undefined}
          onClose={closeEditLineItemSidebar}
          linkedMaterialsReadOnly
        />
      </Card.Body>
    </Card>
  );
};

export default PreferredProductsSection;
